
import { Environment } from "../../../environments/Environment";
import { LocalStorageKeyEnum } from "./LocalStorageKeyEnum";
import { BaseTool } from "../base/service/BaseTool";
import { appConfig } from "@env/app-config";
import { Store } from "@modules/store/stores/model/domain/Store";
import is from "@core/comparators/is";

/**
 * @description 公用数据共享,如LocalStorage緩存
 * @deprecated
 * @author Roman
 * @date 2021-9-26
 */
export class RuntimeContext {

    public static readonly $inject: string[] = ["restRuntime"];


    constructor(private restRuntime: qpmn.common.rest.IRestRuntime) {

    }

    /**
     * @description 通过指定key,从localStorage中读取缓存
     * @param key 
     * @returns 
     */
    private getCache(key: string): string | null {
        return localStorage.getItem(appConfig.appName + "-" + key);
    }

    /**
     * @description 将内容作为缓存设置至localStorage
     * @param key 
     * @param value 
     */
    private setCache(key: string, value: string): void {
        localStorage.setItem(appConfig.appName + "-" + key, value);
    }

    /**
     * @description 删除缓存
     * @param key 
     * @param value 
     */
    private removeCache(key: string): void {
        localStorage.removeItem(appConfig.appName + "-" + key);
    }

    private getCountryCode(callback: (code: string) => void) {
        let countryCode = this.getCache(LocalStorageKeyEnum.COUNTRYCODE);
        if (!countryCode) {
            // 获取ip
            BaseTool.getUserIp((code) => {
                // 获取地区code
                BaseTool.getCountryCode((code: string) => {
                    this.setCache(LocalStorageKeyEnum.COUNTRYCODE, code);
                    callback(code);
                })
            });
        } else {
            callback(countryCode);
        }
    }



    /**
     * @description 清除登录状态
     */
    public clearLogin(): void {
        this.removeCache(LocalStorageKeyEnum.USER_NAME);
        this.removeCache(LocalStorageKeyEnum.TOKEN);
        this.removeCache(LocalStorageKeyEnum.TOKEN_TYPE);
        this.removeCache(LocalStorageKeyEnum.CART_NUMBER);
        this.removeCache(LocalStorageKeyEnum.EXPIRE);
        // 清空店铺列表缓存数据
        this.clearStoreList();

        this.setCache(LocalStorageKeyEnum.IS_ANONYMOUS, "true");
    }

    /**
     * @description 合并购物车后需主动清除本地匿名用户信息
     */
    public clearAnonymousInfo(): void {
        this.removeCache(LocalStorageKeyEnum.ANONYMOUS_USER_ID);
    }

    /************************************************************************************************************************/

    /**
     * @description 获取当前用户名称
     */
    public getUserName(): string {
        // 读取缓存
        let userStorageStr = this.getCache(LocalStorageKeyEnum.USER_NAME);

        if (userStorageStr != null) {
            return userStorageStr;
        }

        return "";
    }
    /**
     * @description 获取当前用户显示名称
     */
    public getFirstName(): string {
        // 读取缓存
        let userStorageStr = this.getCache(LocalStorageKeyEnum.FIRSTNAME);

        if (userStorageStr != null) {
            return userStorageStr;
        }

        return "";
    }

    /**
     * @description 获取过期日期
     * @returns 
     */
    public getExpireDate(): string {
        return this.getCache(LocalStorageKeyEnum.EXPIRE) || "";
    }

    /**
     * @description 获取当前用户Token
     * 实名登录时,返回实名用户token,匿名登录时,返回匿名token
     * 从业务需求来说,无token的情况(既没有实名也没有匿名token)
     */
    public getToken(): string | null {
        return this.getCache(LocalStorageKeyEnum.TOKEN);
    }

    /**
     * @description 获取当前用户Token类型
     */
    public getTokenType(): string | null {
        return this.getCache(LocalStorageKeyEnum.TOKEN_TYPE);
    }

    public isExpire(): boolean {
        let currentDate = new Date().getTime();
        return Number(this.getExpireDate()) < currentDate;
    }

    /**
     * @description 将购物车项数量设置至缓存
     * @param qty 
     */
    public setCartNumber(qty: number): void {
        this.setCache(LocalStorageKeyEnum.CART_NUMBER, qty.toString());
    }

    /**
     * @description 从缓存读取购物车项数量
     * @param qty 
     */
    public getCartNumber(): number {
        let cartNumberStr = this.getCache(LocalStorageKeyEnum.CART_NUMBER);

        if (cartNumberStr != null) {
            return new Number(cartNumberStr).valueOf();
        }

        return 0;
    }


    /**
     * 
     * @description 登录成功后,记录当前用户信息
     * 登录分为匿名用户与实名用户
     * @param firstName 用于显示的用户名称
     * @param userName 用于登录的用户名
     * @param token
     * @param tokenType
     * @param isAnonymous
     * @param anonymousUserId: string
     * @param expire 过期时间 
     */
    public setLogin(firstName: string, userName: string, token: string, tokenType: string, isAnonymous: boolean, anonymousUserId?: string, expire?: number): void {
        this.setCache(LocalStorageKeyEnum.FIRSTNAME, firstName);
        this.setCache(LocalStorageKeyEnum.USER_NAME, userName);
        this.setCache(LocalStorageKeyEnum.TOKEN, token);
        this.setCache(LocalStorageKeyEnum.TOKEN_TYPE, tokenType);
        this.setCache(LocalStorageKeyEnum.IS_ANONYMOUS, isAnonymous.toString());

        if (expire) {
            let currentDate = new Date().getTime();
            let expireDate = expire * 1000 + currentDate;
            this.setCache(LocalStorageKeyEnum.EXPIRE, expireDate.toString());
        }

        if (anonymousUserId) {
            this.setCache(LocalStorageKeyEnum.ANONYMOUS_USER_ID, anonymousUserId);
        }
        // 清空店铺列表缓存
        this.clearStoreList();
    }

    /**
     * @description 检查当前token是否是匿名token
     */
    public isAnonymous(): boolean {
        let isAnonymousStorage = this.getCache(LocalStorageKeyEnum.IS_ANONYMOUS);

        if (isAnonymousStorage != null) {
            return isAnonymousStorage == "true";
        }

        return true;
    }

    /**
     * @description 获取当前用户ID
     */
    public getAnonymousUserId(): number | null {
        // todo: 70或73环境标识
        let anonymousUserIdStorage = this.getCache(LocalStorageKeyEnum.ANONYMOUS_USER_ID);

        if (anonymousUserIdStorage != null) {
            return new Number(anonymousUserIdStorage).valueOf();
        }

        return null;
    }

    /**
     * @description 获取所有LocalStorage缓存内容,并存至当前上下文
     */
    public getAllCache() {

        let storageLength = window.localStorage.length;

        // 遍历整个localStorage,根据下标获取对应的key
        for (let i = 0; i < storageLength; i++) {

            let storageKey = window.localStorage.key(i);

            if (!storageKey) {
                continue;
            }
            // 根据storage key获取缓存,并存至当前上下文
            this[storageKey] = window.localStorage.getItem(storageKey);
        }
    }

    /**
     * @description
     */
    public initRuntimeContext() {
        // 获取所有缓存,并存至当前上下文
        this.getAllCache();

        // 获取token
        let token = this.getToken();
        let tokenType = this.getTokenType();

        if (token != null && tokenType != null) {
            // 将token信息存储至restRuntime
            this.restRuntime.token = {
                token_type: tokenType,
                access_token: token
            }
        }

        // 设置rootPath
        if (/\/stage\//.test(window.location.pathname)) {
            var result = /^https?:\/\/([a-zA-Z0-9\.:])*(\/stage)?\//.exec(window.location.href);
            if (result && result.length > 0) {
                this.restRuntime.rootPath = result[0];
            }
        }

        // 设置是否采用test接口:id地址|localhost|stage环境皆采用测试接口
        if (/\/stage/.test(window.location.pathname) || /(localhost|[0-9\.]+)[\:0-9]*/gi.test(window.location.host)) {
            this.restRuntime.useTestVersion = true;
        }

    }

    /**
     * @description 设置
     * @param isAnonymous 
     */
    public setAnonymous(isAnonymous: boolean, anonymousUserId?: string) {
        this.setCache(LocalStorageKeyEnum.IS_ANONYMOUS, isAnonymous.toString());

        if (anonymousUserId) {
            this.setCache(LocalStorageKeyEnum.ANONYMOUS_USER_ID, anonymousUserId);
        }
    }

    /**
     * @description 设置店铺列表数据
     * @param storeList
     */
    public setStoreList(storeList: string) {
        this.setCache(LocalStorageKeyEnum.STORE_LIST, JSON.stringify(storeList));
    }

    /**
     * @description 获取店铺列表数据
     * @param storeList
     */
    public getStoreList(): string | undefined {
        const storeListStr = this.getCache(LocalStorageKeyEnum.STORE_LIST) as string;
        return (is.undefined(storeListStr) || storeListStr === "") ? undefined : storeListStr;
    }

    /**
     * @description 设置店铺列表数据-过期时间
     */
    public setStoreListExpireDate(storeList: Array<Store>) {
        const currentDate = new Date().getTime();
        const expireDate = currentDate + (15 * 60 * 1000);
        this.setCache(LocalStorageKeyEnum.STORE_LIST_EXPIRE, String(expireDate));
    }

    /**
     * @description 获取店铺列表数据-过期时间
     * @param storeList
     */
    public getStoreListExpireDate(): number | undefined {
        const expireDate = this.getCache(LocalStorageKeyEnum.STORE_LIST_EXPIRE) as string;
        return (is.undefined(expireDate) || expireDate === "") ? undefined : Number(expireDate);
    }

    /**
     * @description 清除店铺列表缓存数据(店铺列表数据，店铺列表数据过期时间)
     */
    public clearStoreList() {
        this.removeCache(LocalStorageKeyEnum.STORE_LIST);
        this.removeCache(LocalStorageKeyEnum.STORE_LIST_EXPIRE);

    }




}